import { useEffect, useState } from "react";
import { Icon } from "../components/Icon.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { useAction } from "./fields.js";
import { useProviderStatus } from "./providers.js";
import type { CostEstimate, JobStatus } from "./types.js";

const JOB_LABEL: Record<JobStatus, string> = {
  queued: "Waiting",
  generating: "Generating",
  done: "Complete",
  error: "Failed",
  canceled: "Canceled"
};

function formatEstimate(e: CostEstimate): string {
  if (e.currency === "USD" && e.min !== null && e.max !== null) {
    return e.min === e.max ? `Estimated generation cost: $${e.min.toFixed(2)}` : `Estimated generation cost: $${e.min.toFixed(2)} - $${e.max.toFixed(2)}`;
  }
  return e.message ?? "Cost depends on your selected provider.";
}

/** Bulk generation controls and the live, persisted queue. */
export function QueueBar() {
  const { projectId, state, reload } = useColoring();
  const { busy, run } = useAction();
  const providers = useProviderStatus();
  const { pages, jobs } = state;
  const [confirm, setConfirm] = useState(false);
  const [estimate, setEstimate] = useState<CostEstimate | null>(null);
  const [showList, setShowList] = useState(true);

  const pageJobs = jobs.filter((j) => pages.some((p) => p.id === j.pageId));
  const open = pageJobs.filter((j) => j.status === "queued" || j.status === "generating");
  const failed = pageJobs.filter((j) => j.status === "error");
  const finished = pageJobs.filter((j) => j.status === "done" || j.status === "canceled");
  const openIds = new Set(open.map((j) => j.pageId));
  const missing = pages.filter((p) => !p.currentVariantId && !openIds.has(p.id));
  const total = pageJobs.length;
  const doneCount = pageJobs.filter((j) => j.status === "done").length;
  const running = open.length > 0;
  const noProvider = providers !== null && !providers.active;

  useEffect(() => {
    if (!confirm) return;
    let alive = true;
    coloringApi.estimate(projectId, missing.length).then((e) => alive && setEstimate(e)).catch(() => alive && setEstimate(null));
    return () => {
      alive = false;
    };
  }, [confirm, projectId, missing.length]);

  if (pages.length === 0) return null;

  const startAll = () => {
    setConfirm(false);
    return run("all", async () => {
      const r = await coloringApi.generateAll(projectId, { onlyMissing: true });
      await reload();
      return r;
    }, undefined);
  };

  return (
    <div className="queue-bar">
      {!running && missing.length > 0 && (
        <div className="queue-ready">
          <div>
            <div className="strong">Ready to generate {missing.length} coloring page{missing.length === 1 ? "" : "s"}.</div>
            <div className="faint" style={{ fontSize: 12 }}>
              Nothing is generated until you start it. Generate them all, or open a page to generate individually.
              {pages.length - missing.length > 0 && ` ${pages.length - missing.length} already ${pages.length - missing.length === 1 ? "has" : "have"} artwork and will be skipped.`}
            </div>
          </div>
          <button className="btn btn-primary" disabled={busy !== null || noProvider} onClick={() => setConfirm(true)} title={noProvider ? "Connect an image provider first" : undefined}>
            <Icon name="sparkles" size={15} /> Generate All
          </button>
        </div>
      )}
      {!running && missing.length > 0 && noProvider && (
        <div className="gen-note warn" style={{ marginTop: 10 }}>
          <Icon name="alert" size={14} />
          <span>No image provider is connected. Add one on the server (see the Style tab) to generate artwork. You can still upload your own page art.</span>
        </div>
      )}

      {(running || failed.length > 0 || finished.length > 0) && (
        <div className="queue-status">
          <div className="row gap-2" style={{ flexWrap: "wrap" }}>
            <strong>Generation queue</strong>
            <span className="faint" style={{ fontSize: 12 }}>
              {doneCount} of {total} complete
              {open.filter((j) => j.status === "generating").length > 0 && " · 1 generating"}
              {open.filter((j) => j.status === "queued").length > 0 && ` · ${open.filter((j) => j.status === "queued").length} waiting`}
              {failed.length > 0 && ` · ${failed.length} failed`}
            </span>
            <div className="spacer" />
            {failed.length > 0 && (
              <button className="btn btn-sm" disabled={busy !== null || noProvider} onClick={() => void run("retry", async () => { await coloringApi.retryFailed(projectId); await reload(); }, "Retrying the failed pages.")}>
                Retry Failed ({failed.length})
              </button>
            )}
            {open.some((j) => j.status === "queued") && (
              <button className="btn btn-sm btn-ghost" disabled={busy !== null} onClick={() => void run("cancel", async () => { const r = await coloringApi.cancelQueue(projectId); await reload(); return r; }, "Canceled the waiting pages. The one in progress will finish.")}>
                Cancel Remaining
              </button>
            )}
            {!running && (
              <button className="btn btn-sm btn-ghost" onClick={() => void run("clear", async () => { await coloringApi.clearQueue(projectId); await reload(); })}>Clear finished</button>
            )}
            <button className="btn btn-icon btn-ghost btn-sm" onClick={() => setShowList((s) => !s)} title={showList ? "Hide details" : "Show details"}>
              <Icon name={showList ? "chevronDown" : "chevronRight"} size={14} />
            </button>
          </div>
          {total > 0 && (
            <div className="progress-track" style={{ marginTop: 10 }}>
              <div className="progress-fill" style={{ width: `${Math.round((doneCount / total) * 100)}%` }} />
            </div>
          )}
          {showList && (
            <ul className="queue-list">
              {pageJobs.map((j) => {
                const index = pages.findIndex((p) => p.id === j.pageId);
                const page = pages[index];
                return (
                  <li key={j.id} className={`queue-row job-${j.status}`}>
                    <span className="queue-dot">
                      {j.status === "generating" ? <Icon name="clock" size={11} className="spin" /> : j.status === "done" ? <Icon name="check" size={11} strokeWidth={2.5} /> : j.status === "error" ? <Icon name="alert" size={11} /> : null}
                    </span>
                    <span className="truncate">Page {index + 1}{page?.title ? ` — ${page.title}` : ""}</span>
                    <span className="queue-state">{JOB_LABEL[j.status]}</span>
                    {j.status === "error" && j.error && <span className="queue-error" title={j.error}>{j.error}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={`Generate ${missing.length} coloring page${missing.length === 1 ? "" : "s"}?`}
          message={`${providers?.active ? `Pages will be generated one at a time with ${providers.active.label}. ` : ""}${(estimate ? formatEstimate(estimate) : "Working out the cost").replace(/\.$/, "")}. If one page fails, the others continue and you can retry it.`}
          confirmLabel="Generate All"
          onConfirm={() => void startAll()}
          onCancel={() => setConfirm(false)}
        />
      )}
    </div>
  );
}
